import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AdesaoService } from '../../../../services/adesao/adesao.service';
import { ComparativoAdesao, PendenciaCartaoSim, RegularizacaoGerada, VencimentoRegularizacao } from '../../../../interfaces/adesao.dto';
import { BrlPipe } from '../../../../utils/brl.pipe';
import { comDesconto, percentualRegua } from './regua-desconto';
import { AcoesCobranca, CobrancaSimComponent } from '../cobranca-sim/cobranca-sim.component';

/**
 * Cliente CartãoSiM em atraso/bloqueado: fatura unificada como no admin, com a régua de desconto quando o operador pode aplicá-la. O
 * pagamento reativa o cliente pelos triggers do banco; sem pagar, segue bloqueado e o
 * atendimento é particular.
 */
@Component({
  selector: 'app-pendencia-sim',
  standalone: true,
  imports: [CommonModule, FormsModule, BrlPipe, CobrancaSimComponent],
  templateUrl: './pendencia-sim.component.html',
  styleUrl: './pendencia-sim.component.scss',
})
export class PendenciaSimComponent implements OnChanges {
  @Input() personId = 0;
  @Input() clinicId = 0;
  @Input() cpf = '';
  @Input() subscriptionId?: number;
  /** Faturas abertas da assinatura segundo o aviso (inclui o que a regularização não lista, como a multa). */
  @Input() pendentesNaAssinatura?: number;
  /** Outras assinaturas canceladas que ainda seguram a adesão, em texto corrido. */
  @Input() outrasPendenciasCancelada?: string;
  @Input() guiche = '';
  @Input() procedimentos: { cdProcedimento: number; quantidade: number }[] = [];
  /** Pausa "Venda Cartão": não há atendimento, então os textos não falam da consulta de hoje. */
  @Input() naPausa = false;
  @Output() temPendencia = new EventEmitter<boolean>();
  @Output() regularizado = new EventEmitter<void>();
  /** Com a pendência a oferta some, e era ela quem dava ao pai o valor particular. */
  @Output() valorParticular = new EventEmitter<number | null>();

  pendencia: PendenciaCartaoSim | null = null;
  comparativo: ComparativoAdesao | null = null;
  selecionadas = new Set<number>();
  vencimento: VencimentoRegularizacao = '';
  editandoVencimento = false;
  ignorada = false;
  gerada: RegularizacaoGerada | null = null;
  etapa: 'lista' | 'cobranca' | 'pago' = 'lista';
  isEnviando = false;
  erro = '';
  clientId: number | null = null;
  reativado = false;
  private carga = 0;
  private cargaComparativo = 0;
  // O pai passa um getter (array novo a cada ciclo): só conteúdo diferente recarrega.
  private chaveComparativo = '';

  constructor(private adesaoService: AdesaoService) {}

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['personId'] && this.personId) {
      this.ignorada = false;
      this.detalheAberto = false;
      this.vencimento = '';
      this.editandoVencimento = false;
      await this.carregar();
      return;
    }
    if (changes['procedimentos'] && this.pendencia && chaveDe(this.procedimentos) !== this.chaveComparativo)
      await this.atualizarComparativo(this.carga);
  }

  /** false quando outra carga (troca de paciente) começou durante a espera. */
  async carregar(): Promise<boolean> {
    const n = ++this.carga;
    this.etapa = 'lista';
    this.gerada = null;
    this.erro = '';
    this.clientId = null;
    this.reativado = false;
    let pendencia = this.personId ? await this.adesaoService.pendencia(this.personId, this.subscriptionId) : null;
    if (n !== this.carga) return false;
    // Fatura zerada por desconto não aparece, mas vai junto: fecha com a unificação sem custo.
    const temSaldo = !!pendencia && (!!pendencia.unificacaoAberta || pendencia.faturas.some(f => f.valor > 0));
    this.pendencia = temSaldo ? pendencia : null;
    this.selecionadas = new Set((this.pendencia?.faturas ?? [])
      .filter(f => this.pendencia!.assinaturaAtiva || this.pendencia!.cancelada || f.valor <= 0).map(f => f.billId));
    this.temPendencia.emit(temSaldo);
    pendencia = this.pendencia;
    if (!pendencia) {
      this.cargaComparativo++;
      this.comparativo = null;
      this.chaveComparativo = '';
      return true;
    }
    await Promise.all([this.carregarCliente(n), this.atualizarComparativo(n)]);
    return n === this.carga;
  }

  private async carregarCliente(n: number): Promise<void> {
    if (!this.cpf) return;
    const clientId = (await this.adesaoService.cliente(this.cpf))?.id ?? null;
    if (n === this.carga) this.clientId = clientId;
  }

  private async atualizarComparativo(n: number): Promise<void> {
    const m = ++this.cargaComparativo;
    this.chaveComparativo = chaveDe(this.procedimentos);
    if (this.cancelada) {
      this.comparativo = null;
      return;
    }
    const comparativo = this.procedimentos.length
      ? await this.adesaoService.comparativoPendencia(this.clinicId, this.personId, this.procedimentos)
      : null;
    if (n !== this.carga || m !== this.cargaComparativo) return;
    this.comparativo = comparativo;
    this.valorParticular.emit(this.bloqueado ? comparativo?.semPlano ?? null : null);
  }

  /** Recarrega mantendo a mensagem de erro, se houver. */
  private async recarregar(erro = ''): Promise<void> {
    if (!(await this.carregar())) return;
    this.erro = erro;
  }

  get temEconomia(): boolean {
    return (this.comparativo?.economia ?? 0) > 0;
  }

  get nomePlano(): string {
    // DS_PLAN vem cru ("CE SIM+ BLACK 5 RECORRENTE SET/24"): mostra só a família.
    const familia = (this.comparativo?.familia ?? '').match(/PLATINA|BLACK|OURO/i)?.[0];
    return familia ? `plano ${familia[0].toUpperCase()}${familia.slice(1).toLowerCase()}` : 'plano';
  }

  /** Só o bloqueado cai para particular; em atraso a trigger ainda dá o plano. */
  get bloqueado(): boolean {
    return this.pendencia?.statusCliente === 3;
  }

  /** O status do cliente foi lido antes de pagar: confirmado o pagamento, o selo vira "Regularizado". */
  get regularizadoOk(): boolean {
    return this.etapa === 'pago' && this.reativado;
  }

  /** Assinatura cancelada: sem plano a reativar, só quitar as mensalidades. */
  get cancelada(): boolean {
    return this.pendencia?.cancelada === true;
  }

  get soTicket(): boolean {
    return this.pendencia?.soTicket === true;
  }

  /** Faturas abertas que o aviso conta e a regularização não lista (multa de cancelamento). */
  get foraDaRegularizacao(): number {
    if (!this.cancelada || this.pendencia?.unificacaoAberta || this.pendentesNaAssinatura == null) return 0;
    return Math.max(0, this.pendentesNaAssinatura - (this.pendencia?.faturas.length ?? 0));
  }

  get notaFechamento(): string {
    if (this.cancelada && this.outrasPendenciasCancelada)
      return `Quitando, ainda falta ${this.outrasPendenciasCancelada} para liberar a adesão`;
    if (this.cancelada)
      return this.foraDaRegularizacao ? 'Quitando, as mensalidades em aberto ficam regularizadas.' : 'Quitando, a adesão a um novo plano fica liberada.';
    if (!this.bloqueado) return 'Deixa a assinatura em dia';
    return this.naPausa ? 'Reativa a assinatura' : 'Reativa a assinatura e a consulta sai pelo ' + this.nomePlano;
  }

  get textoTopo(): string {
    const enel = 'As faturas do Plano Enel só se regularizam por ticket para o financeiro.';
    if (this.naPausa) {
      const situacao = this.bloqueado ? 'Cliente bloqueado.' : 'Cliente em atraso.';
      if (this.soTicket) return `${situacao} ${enel}`;
      return this.bloqueado ? `${situacao} Regularizando, a assinatura é reativada.` : `${situacao} Regularize para a assinatura não ser bloqueada.`;
    }
    if (this.soTicket)
      return `${this.bloqueado ? 'Cliente bloqueado: o atendimento de hoje sai como particular.' : 'O atendimento de hoje já sai pelo plano.'} ${enel}`;
    return this.bloqueado
      ? 'Cliente bloqueado: o atendimento de hoje sai como particular. Regularizando, a assinatura é reativada e a consulta sai pelo ' + this.nomePlano + '.'
      : 'O atendimento de hoje já sai pelo plano. Regularize para a assinatura não ser bloqueada.';
  }

  get rodapeCobranca(): string {
    if (this.cancelada) return 'Sem pagamento, a adesão a um novo plano segue bloqueada.';
    if (this.naPausa) return this.bloqueado ? 'Sem pagamento, o cliente continua bloqueado.' : 'Sem pagamento, a assinatura continua em atraso.';
    return this.bloqueado
      ? 'Sem pagamento, o cliente continua bloqueado e o atendimento segue como particular.'
      : 'Sem pagamento, a assinatura continua em atraso; o atendimento segue pelo plano.';
  }

  /** Linha da assinatura ativa: começa recolhida (decisão de 03/11/2026). */
  detalheAberto = false;

  get rotuloPendentes(): string {
    if (this.pendencia?.unificacaoAberta) return 'Fatura unificada em aberto';
    const n = this.faturasComSaldo.length;
    return `${n} - ${n === 1 ? 'Fatura pendente' : 'Faturas pendentes'}`;
  }

  get rotuloQuite(): string {
    const n = this.qtdSelecionadasComSaldo;
    return n === 1 ? 'Quite a 1 mensalidade selecionada' : `Quite as ${n} mensalidades selecionadas`;
  }

  get rotuloIgnorar(): string {
    if (this.cancelada || this.naPausa) return 'Agora não';
    return this.bloqueado ? 'Ignorar e seguir como particular' : 'Ignorar e seguir';
  }

  /** dd/mm/aaaa → dd/mm */
  diaMes(data: string): string {
    return (data ?? '').slice(0, 5);
  }

  private static readonly MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho',
    'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

  /** dd/mm/aaaa → "Agosto de 2026" (mês da mensalidade). */
  mesDaFatura(data: string): string {
    const [, mes, ano] = (data ?? '').split('/').map(Number);
    const nome = PendenciaSimComponent.MESES[(mes ?? 0) - 1];
    return nome ? `${nome[0].toUpperCase()}${nome.slice(1)} de ${ano}` : data;
  }

  vencida(data: string, hoje: Date = new Date()): boolean {
    const [dia, mes, ano] = (data ?? '').split('/').map(Number);
    if (!dia || !mes || !ano) return false;
    return new Date(ano, mes - 1, dia) < new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  }

  voltarDaCobranca(): Promise<void> {
    return this.recarregar();
  }

  get selecaoTravada(): boolean {
    return this.pendencia?.assinaturaAtiva === true;
  }

  get faturasComSaldo() {
    return (this.pendencia?.faturas ?? []).filter(f => f.valor > 0);
  }

  get qtdSelecionadasComSaldo(): number {
    return this.faturasComSaldo.filter(f => this.selecionadas.has(f.billId)).length;
  }

  get totalSelecionado(): number {
    const cents = (this.pendencia?.faturas ?? [])
      .filter(f => this.selecionadas.has(f.billId))
      .reduce((s, f) => s + Math.round(f.valor * 100), 0);
    return cents / 100;
  }

  get percentual(): number {
    return this.pendencia?.podeDarDesconto ? percentualRegua(this.qtdSelecionadasComSaldo) : 0;
  }

  /** Régua devida, mas o operador não tem permissão para aplicá-la. */
  get descontoBloqueado(): boolean {
    return !this.pendencia?.podeDarDesconto && percentualRegua(this.qtdSelecionadasComSaldo) > 0;
  }

  /** O que a régua daria se o operador tivesse a permissão (vai no alerta, para o ticket). */
  get percentualSemPermissao(): number {
    return this.descontoBloqueado ? percentualRegua(this.qtdSelecionadasComSaldo) : 0;
  }

  get economiaSemPermissao(): number {
    const p = this.percentualSemPermissao;
    return p ? +(this.totalSelecionado - comDesconto(this.totalSelecionado, p)).toFixed(2) : 0;
  }

  get totalComDesconto(): number {
    return comDesconto(this.totalSelecionado, this.percentual);
  }

  get economia(): number {
    return Math.round((this.totalSelecionado - this.totalComDesconto) * 100) / 100;
  }

  valorComDesconto(f: { valor: number }): number {
    return comDesconto(f.valor, this.percentual);
  }

  static readonly MAX_DIAS_VENCIMENTO = 30;

  /** Data local como yyyy-MM-dd, `dias` a partir de hoje. */
  isoEm(dias: number, hoje: Date = new Date()): string {
    const d = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + dias);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  get vencimentoMin(): string { return this.isoEm(1); }
  get vencimentoMax(): string { return this.isoEm(PendenciaSimComponent.MAX_DIAS_VENCIMENTO); }

  get vencimentoValido(): boolean {
    return !this.vencimento || (this.vencimento >= this.vencimentoMin && this.vencimento <= this.vencimentoMax);
  }

  /** dd/mm/aaaa do vencimento que vai para a fatura. */
  get vencimentoExibido(): string {
    const [a, m, d] = (this.vencimento || this.vencimentoMin).split('-');
    return `${d}/${m}/${a}`;
  }

  restaurarVencimento(): void {
    this.vencimento = '';
    this.editandoVencimento = false;
  }

  get podeGerar(): boolean {
    if (!this.pendencia || this.isEnviando || this.totalSelecionado <= 0 || !this.vencimentoValido) return false;
    return !this.selecaoTravada || this.selecionadas.size === this.pendencia.faturas.length;
  }

  get masterBillId(): number | null {
    return this.gerada?.masterBillId ?? this.pendencia?.unificacaoAberta?.billId ?? null;
  }

  alternar(billId: number): void {
    if (this.selecaoTravada) return;
    this.selecionadas.has(billId) ? this.selecionadas.delete(billId) : this.selecionadas.add(billId);
  }

  get todasSelecionadas(): boolean {
    return this.faturasComSaldo.every(f => this.selecionadas.has(f.billId));
  }

  /** Só mexe nas faturas com saldo: as zeradas seguem marcadas, como na carga. */
  alternarTodas(): void {
    if (this.selecaoTravada) return;
    const marcar = !this.todasSelecionadas;
    for (const f of this.faturasComSaldo) marcar ? this.selecionadas.add(f.billId) : this.selecionadas.delete(f.billId);
  }

  async gerar(): Promise<void> {
    if (!this.podeGerar || !this.pendencia) return;
    this.isEnviando = true;
    this.erro = '';
    try {
      this.gerada = await this.adesaoService.regularizar(this.personId, [...this.selecionadas], this.vencimento, this.subscriptionId);
      this.etapa = 'cobranca';
    } catch (e: any) {
      await this.recarregar(e?.message ?? 'Não foi possível gerar a fatura unificada.');
    } finally {
      this.isEnviando = false;
    }
  }

  async reverter(): Promise<void> {
    const master = this.pendencia?.unificacaoAberta?.billId;
    if (!master || this.isEnviando) return;
    this.isEnviando = true;
    this.erro = '';
    try {
      this.pendencia = await this.adesaoService.reverterRegularizacao(this.personId, master, this.subscriptionId);
      this.selecionadas = new Set(this.pendencia?.assinaturaAtiva || this.pendencia?.cancelada ? this.pendencia.faturas.map(f => f.billId) : []);
      this.temPendencia.emit(!!this.pendencia);
    } catch (e: any) {
      await this.recarregar(e?.message ?? 'Não foi possível reverter a fatura unificada.');
    } finally {
      this.isEnviando = false;
    }
  }

  cobrarAberta(): void {
    this.etapa = 'cobranca';
  }

  /** Pago não garante reativação: só emite com o cliente já ativo no banco. */
  aguardandoReativacao = false;
  // A reativação vem dos triggers depois do webhook: espera como a adesão espera a ativação.
  esperaReativacaoMs = 5000;
  tentativasReativacao = 6;

  async onPago(): Promise<void> {
    if (this.etapa === 'pago') return;
    const n = this.carga;
    this.etapa = 'pago';
    this.aguardandoReativacao = true;
    let ok = false;
    for (let i = 0; i < this.tentativasReativacao && !ok; i++) {
      if (i > 0) await new Promise(r => setTimeout(r, this.esperaReativacaoMs));
      if (n !== this.carga) return;
      try {
        ok = this.cancelada ? await this.quitada() : (await this.adesaoService.ativacao(this.personId))?.situacao === 'ativo';
      } catch {
        ok = false;
      }
    }
    if (n !== this.carga) return;
    this.aguardandoReativacao = false;
    this.reativado = ok;
    if (ok) this.regularizado.emit();
  }

  /** Cancelada não reativa: está regularizada quando a pendência da assinatura some. */
  private async quitada(): Promise<boolean> {
    const p = await this.adesaoService.pendencia(this.personId, this.subscriptionId);
    return !p || (!p.unificacaoAberta && !p.faturas.some(f => f.valor > 0));
  }

  readonly acoes: AcoesCobranca = {
    cobrarNoTerminal: poiid => this.adesaoService.cobrarFaturaNoTerminal(this.masterBillId!, poiid, this.clinicId),
    gerarLink: () => this.adesaoService.linkFatura(this.masterBillId!),
    enviarLink: (url, email, whatsApp) =>
      this.adesaoService.enviarLinkFatura(this.masterBillId!, this.clientId!, url, this.clinicId, email, whatsApp),
    foiPago: () => this.adesaoService.faturaPaga(this.masterBillId!),
  };

  get valorCobranca(): number | null {
    return this.gerada?.valor ?? this.pendencia?.unificacaoAberta?.valor ?? null;
  }
}

function chaveDe(itens: { cdProcedimento: number; quantidade: number }[]): string {
  return (itens ?? []).map(i => `${i.cdProcedimento}x${i.quantidade}`).join(',');
}
